/**
 * Tournament draw seeding utilities
 * Pure functions for seeding players in tournament draws
 */

import { getDrawPositions, SUPPORTED_DRAW_SIZES, DRAW_POSITION_CONFIGS } from '../core/constants';
import { CustomPlateSlot, Player } from '../types';

/**
 * Create a BYE player with consistent properties
 * @param id - Unique identifier for the BYE
 * @param position - Position in the draw (optional)
 * @returns BYE player object
 */
export function createByePlayer(id: string, position?: number): Player {
  return {
    id,
    firstName: 'BYE',
    lastName: '',
    position,
    isBye: true,
  };
}

/**
 * Calculate which positions should be byes in a tournament draw
 * Byes are the positions that would be occupied by seeds beyond the total player count
 *
 * @param drawSize - The draw size (4, 8, 16, 32, 64, 128)
 * @param totalPlayers - Total number of players in the draw
 * @returns Array of position numbers that should be byes
 */
export function calculateByePositions(drawSize: number, totalPlayers: number): number[] {
  const byeCount = drawSize - totalPlayers;
  if (byeCount <= 0) {
    return []; // No byes needed
  }

  const config = DRAW_POSITION_CONFIGS[drawSize];
  if (!config) {
    throw new Error(`Unsupported draw size: ${drawSize}`);
  }

  // Byes are the positions that would be occupied by seeds beyond the total player count
  // For example: 24 players in 32-draw means seeds 25-32 become byes
  const byePositions: number[] = [];

  // Get positions for seeds that would be beyond the total player count
  for (let seed = totalPlayers + 1; seed <= drawSize; seed++) {
    // Find the position for this seed in the draw configuration
    const positionIndex = seed - 1; // Convert to 0-based index
    if (positionIndex < config.positions.length) {
      byePositions.push(config.positions[positionIndex]);
    }
  }

  return byePositions.sort((a, b) => a - b);
}

/**
 * Lay eligible losers into a custom plate's first round, leaving BYEs where the
 * main draw would put them (the seats of the missing seeds) rather than piling
 * them up at the bottom. Losers keep their order; seats not returned are BYEs.
 *
 * @param losers - Source matches whose losers feed the plate, in bracket order
 * @param drawSize - The plate's draw size
 * @returns One routing slot per placed loser (at most drawSize)
 */
export function distributePlateLosers(
  losers: Array<Pick<CustomPlateSlot, 'sourceRound' | 'sourceMatchPosition'>>,
  drawSize: number,
): CustomPlateSlot[] {
  const placed = losers.slice(0, drawSize);
  const byePositions = new Set(calculateByePositions(drawSize, placed.length));

  const slots: CustomPlateSlot[] = [];
  let next = 0;
  for (let position = 1; position <= drawSize && next < placed.length; position++) {
    if (byePositions.has(position)) continue;
    const { sourceRound, sourceMatchPosition } = placed[next++];
    slots.push({
      sourceRound,
      sourceMatchPosition,
      plateMatchPosition: Math.ceil(position / 2),
      playerSlot: position % 2 === 1 ? 'player1' : 'player2',
    });
  }
  return slots;
}

/**
 * Seed players for a tournament draw with proper bye handling
 *
 * @param players - Array of players to seed (should be sorted by ranking/rating)
 * @param drawSize - The draw size (must be a supported size: 4, 8, 16, 32, 64, 128)
 * @param seededCount - Number of top players to seed (must be <= drawSize)
 * @param randomCount - Number of players to place randomly (must be <= remaining players)
 * @returns Array of seeded players with position and group information
 */
export function seedPlayers(
  players: Player[],
  drawSize: number,
  seededCount: number,
  randomCount: number,
): Player[] {
  // Validation
  if (!SUPPORTED_DRAW_SIZES.includes(drawSize)) {
    throw new Error(
      `Unsupported draw size: ${drawSize}. Supported sizes: ${SUPPORTED_DRAW_SIZES.join(', ')}`,
    );
  }

  if (seededCount < 0 || randomCount < 0) {
    throw new Error('Seeded count and random count must be non-negative');
  }

  if (seededCount + randomCount > drawSize) {
    throw new Error(
      `Total players (${seededCount + randomCount}) cannot exceed draw size (${drawSize})`,
    );
  }

  if (players.length < seededCount + randomCount) {
    throw new Error(
      `Not enough players (${players.length}) for requested seeded (${seededCount}) + random (${randomCount}) count`,
    );
  }

  const totalPlayers = seededCount + randomCount;

  // Get draw configuration for the full draw size
  const drawConfig = getDrawPositions(drawSize, drawSize);

  // Calculate which positions should be byes
  const byePositions = calculateByePositions(drawSize, totalPlayers);

  // Take the first N players as seeded (assuming they're already sorted by ranking)
  const seededPlayers = players.slice(0, seededCount);

  // Take the next M players as random
  const randomPlayers = players.slice(seededCount, seededCount + randomCount);

  // Shuffle random players for random placement
  const shuffledRandomPlayers = [...randomPlayers].sort(() => Math.random() - 0.5);

  const result: Player[] = [];

  // Assign seeded positions (seeds 1, 2, 3, 4, ...)
  for (let i = 0; i < seededPlayers.length; i++) {
    const player = seededPlayers[i];
    const seedNumber = i + 1; // Sequential seeds: 1, 2, 3, 4, ...

    result.push({
      id: player.id,
      firstName: player.firstName,
      lastName: player.lastName,
      seed: seedNumber,
      seedGroup: drawConfig.groups[i],
      pointsAtSeeding: player.pointsAtSeeding,
      position: drawConfig.positions[i],
      group: drawConfig.groups[i],
    });
  }

  // Assign random positions to unseeded players (no seed number or group)
  // Unseeded players get positions that would be seeds 9-24 (for 24 players in 32-draw)
  for (let i = 0; i < shuffledRandomPlayers.length; i++) {
    const player = shuffledRandomPlayers[i];
    const seedNumber = seededCount + i + 1; // This would be seed 9, 10, 11, etc.
    const positionIndex = seedNumber - 1; // Convert to 0-based index

    result.push({
      id: player.id,
      firstName: player.firstName,
      lastName: player.lastName,
      pointsAtSeeding: player.pointsAtSeeding,
      // No seed or group for unseeded players
      position: drawConfig.positions[positionIndex],
    });
  }

  // Add bye positions
  for (const byePosition of byePositions) {
    result.push(createByePlayer(`bye-${byePosition}`, byePosition));
  }

  return result;
}

/**
 * Get the tournament position for a given seed number in a draw
 * This function converts a seed number (1-based) to the actual bracket position
 *
 * @param seedNumber - The seed number (1-based, e.g., 1, 2, 3, 4...)
 * @param drawSize - The draw size (4, 8, 16, 32, 64, 128)
 * @returns The tournament position (1-based) where this seed should be placed, or null if invalid
 */
export function getSeedPosition(seedNumber: number, drawSize: number): number | null {
  if (seedNumber < 1 || seedNumber > drawSize) {
    return null;
  }

  const drawConfig = getDrawPositions(drawSize, drawSize);

  // Find the position for this seed number in the draw configuration
  const positionIndex = drawConfig.positions.indexOf(seedNumber);

  if (positionIndex === -1) {
    return null;
  }

  // Return 1-based position
  return positionIndex + 1;
}
